import useSWR from "swr"
import { api_fetch } from "@/api/client"
import { use_debounced_value } from "@/hooks/use_debounced_value"
import { exactCurrencyFormatter } from "@/utils"
import { Promotion } from "../types"
import { LastRun, LastRunReference, describe_runs, last_run_message, last_run_warning } from "../last_run_check"

/** Query for `GET /promotions/last-run`, or null when the form does not yet name an event and account. */
export function last_run_params(promotion: Promotion): Record<string, string> | null {
  const name = promotion.name?.trim() ?? ""
  if (name.length < 3 || !(promotion.retailer || promotion.customer)) return null
  const params: Record<string, string> = { name }
  if (promotion.retailer) params.retailer = promotion.retailer
  if (promotion.customer) params.customer = promotion.customer
  if (promotion.promotion_type) params.promotion_type = promotion.promotion_type
  if (promotion.start_date) params.start_date = promotion.start_date
  if (promotion.id) params.exclude_id = promotion.id
  return params
}

/** What the same event spent the last times it ran, for the promotion as the form has it. */
export function use_last_run_reference(promotion: Promotion): LastRunReference | null {
  // Debounce the serialized query: a fresh object every render would never settle.
  const params = last_run_params(promotion)
  const key = use_debounced_value(params ? JSON.stringify(params) : null, 400)
  const { data } = useSWR(key ? ["promotions-last-run", key] : null, async ([, query]: [string, string]) => {
    const result = await api_fetch<LastRunReference | null>("/promotions/last-run", { params: JSON.parse(query) })
    return result.ok ? result.value.data : null
  })
  return data ?? null
}

interface LastRunNoticeProps {
  warning: ReturnType<typeof last_run_warning>
  className?: string
}

/** The amber note under Planned Spend when a plan is far from what the event did last time. */
export function LastRunNotice({ warning, className = "" }: LastRunNoticeProps) {
  if (!warning) return null
  return (
    <div class={`rounded-md border border-amber-300 bg-amber-50 p-3 ${className}`} data-testid="last-run-notice">
      <p class="text-sm font-medium text-amber-800">{last_run_message(warning, exactCurrencyFormatter)}</p>
      <p class="mt-1 text-xs text-amber-700">
        Compared with:{" "}
        {warning.reference.runs.map((run: LastRun, index: number) => (
          <span key={run.id}>
            {index > 0 ? ", " : ""}
            <a class="underline hover:text-amber-900" href={`/trade-promotion-management?promotion=${run.id}`} target="_blank" rel="noopener noreferrer">
              {run.name}
            </a>{" "}
            ({exactCurrencyFormatter(run.actual)})
          </span>
        ))}
      </p>
    </div>
  )
}

/** Fetches the reference for the promotion being edited and shows the note when it applies. */
export function PromotionLastRunNotice({ promotion, className }: { promotion: Promotion; className?: string }) {
  const reference = use_last_run_reference(promotion)
  return <LastRunNotice warning={last_run_warning(promotion.planned_amount, reference)} className={className} />
}

/** Plain-text detail of the runs, for a tooltip. */
export function last_run_title(reference: LastRunReference): string {
  return `Compared with ${describe_runs(reference, exactCurrencyFormatter)}`
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("last_run_params", () => {
    it("asks only once the form names the event and an account", () => {
      expect(last_run_params({ name: "Oc", products: [], retailer: "Whole Foods" })).toBeNull()
      expect(last_run_params({ name: "October B - Off Shelf", products: [] })).toBeNull()
    })

    it("sends what the server matches on, and excludes the promotion itself when editing", () => {
      expect(
        last_run_params({
          id: "p1",
          name: " October B - Off Shelf - Frozen Entrees - WFM 2026 ",
          products: [],
          retailer: "Whole Foods",
          customer: "UNFI",
          promotion_type: "Retailer price reduction",
          start_date: "2026-10-21",
        }),
      ).toEqual({
        name: "October B - Off Shelf - Frozen Entrees - WFM 2026",
        retailer: "Whole Foods",
        customer: "UNFI",
        promotion_type: "Retailer price reduction",
        start_date: "2026-10-21",
        exclude_id: "p1",
      })
    })
  })
}
